import React, { useState, useEffect } from 'react';
import { resolveMediaUrl } from '../Common/DetailParts';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RequirementOptionsPanel, requirementsMessage } from '../Common/requirements';
import { memberApplicationAPI } from '../../../services/membershipApi';
import DocumentViewerModal from '../Common/DocumentViewerModal';

const PublicMembershipDocumentSubmit = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingSlots, setUploadingSlots] = useState({}); // { [docName]: boolean }
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerName, setViewerName] = useState('');

  useEffect(() => {
    fetchApplication();
  }, [token]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await memberApplicationAPI.getPublicApplicationByToken(token);
      setApplication(res.data?.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Invalid or expired document submission link.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (docName, e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = ''; // reset

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', docName);

    setUploadingSlots(prev => ({ ...prev, [docName]: true }));
    try {
      const res = await memberApplicationAPI.uploadPublicDocument(token, formData);
      setApplication(prev => ({
        ...prev,
        documents: res.data?.data || []
      }));
      toast.success(`"${docName}" uploaded successfully`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to upload document.');
    } finally {
      setUploadingSlots(prev => ({ ...prev, [docName]: false }));
    }
  };

  const handleDelete = async (docName) => {
    if (!window.confirm(`Are you sure you want to delete the uploaded document for "${docName}"?`)) {
      return;
    }

    try {
      const res = await memberApplicationAPI.deletePublicDocument(token, docName);
      setApplication(prev => ({
        ...prev,
        documents: res.data?.data || []
      }));
      toast.success(`"${docName}" removed`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to delete document.');
    }
  };

  const handleSubmit = async () => {
    // Check if all required documents are uploaded
    // Any one requirement option fully uploaded is enough (or every document, for a type without options)
    const docProblem = requirementsMessage(application, (application?.documents || []).map(d => d.name));
    if (docProblem) {
      toast.error(docProblem, { duration: 7000 });
      return;
    }

    setIsSubmitting(true);
    try {
      await memberApplicationAPI.submitPublicDocuments(token);
      setSuccess(true);
      toast.success('Documents submitted successfully!');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Submission failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <svg className="w-10 h-10 animate-spin text-violet-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-gray-500 font-medium">Loading your application details</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl p-8 border border-gray-100 shadow-2xl text-center space-y-6">
          <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-xl flex items-center justify-center mx-auto">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-medium text-gray-900">Access Denied</h1>
            <p className="text-sm text-gray-500 mt-2">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl p-8 border border-gray-100 shadow-2xl text-center space-y-6">
          <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-xl flex items-center justify-center mx-auto">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-medium text-gray-950">Submission Received</h1>
            <p className="text-sm text-slate-500 mt-2">
              Thank you! Your required documents have been uploaded successfully and submitted for review.
            </p>
          </div>
          <div className="pt-2">
            <p className="text-xs text-slate-400">Application Number: <span className="font-medium">{application?.applicationNumber}</span></p>
          </div>
        </div>
      </div>
    );
  }

  const requiredDocs = application?.requiredDocuments || [];
  const uploadedDocs = application?.documents || [];
  const isLocked = ['approved', 'pending_committee'].includes(application?.status);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-start py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full mx-auto space-y-8">
        
        {/* Header Branding */}
        <div className="text-center">
          <div className="w-12 h-12 bg-gradient-to-tr from-violet-600 to-indigo-600 rounded-xl shadow-lg flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-medium text-xl">S</span>
          </div>
          <h2 className="text-2xl font-medium text-slate-900">SLIM Member Portal</h2>
          <p className="text-sm text-slate-500 mt-1">Submit Required Documents for Your Application</p>
        </div>

        {/* Application Summary Card */}
        <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-xl space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-medium text-slate-800 text-lg">
                {application?.firstName} {application?.lastName}
              </h3>
              <p className="text-sm text-slate-400 mt-0.5">{application?.email}</p>
            </div>
            <div className="text-right">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-55 border border-violet-100 text-violet-75">
                {application?.applicationNumber}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 pt-4 border-t border-slate-50 text-sm">
            <div>
              <span className="text-xs text-slate-400 font-medium">Membership Category</span>
              <p className="font-medium text-slate-700 mt-0.5">{application?.membershipTypeName}</p>
            </div>
            <div>
              <span className="text-xs text-slate-400 font-medium">Review Status</span>
              <p className="mt-0.5">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize border ${
 isLocked 
 ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
 : 'bg-amber-100 text-amber-700 border-amber-200'
 }`}>
                  {application?.status?.replace('_', ' ')}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Document Upload Card */}
        <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-xl space-y-6">
          <div>
            <h4 className="font-medium text-slate-800 text-base">Required Documents</h4>
            <p className="text-xs text-slate-400 mt-0.5">
              Please upload clear PDF, JPG, or PNG copies (max 5MB each).
            </p>
          </div>

          {/* Profile Picture Upload Section */}
          <div
            className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
 application?.profilePicture
 ? 'bg-emerald-50/50 border-emerald-100'
 : 'bg-slate-50/50 border-slate-200 border-dashed'
 }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              {application?.profilePicture ? (
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-emerald-500 bg-slate-100 flex-shrink-0 relative">
                  <img src={resolveMediaUrl(application.profilePicture)} alt="Profile" className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 right-0 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center text-white border border-white">
                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              ) : (
                <div className="w-14 h-14 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center flex-shrink-0">
                  <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
              <div className="min-w-0">
                <p className={`text-sm font-medium truncate ${application?.profilePicture ? 'text-emerald-800' : 'text-slate-700'}`}>
                  Profile Picture
                </p>
                {application?.profilePicture ? (
                  <p className="text-xs text-emerald-600 font-medium">Uploaded</p>
                ) : (
                  <p className="text-xs text-slate-400 font-medium">Pending Upload</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0 ml-3">
              {application?.profilePicture && (
                <button
                  type="button"
                  onClick={() => {
                    setViewerUrl(application.profilePicture);
                    setViewerName('Profile Picture');
                    setViewerOpen(true);
                  }}
                  className="p-1.5 text-slate-400 hover:text-indigo-600 transition-colors"
                  title="View Profile Picture"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              )}
              {application?.profilePicture && !isLocked && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm('Are you sure you want to remove your profile picture?')) return;
                    try {
                      const res = await memberApplicationAPI.deletePublicDocument(token, 'profilePicture');
                      setApplication(prev => ({
                        ...prev,
                        documents: res.data?.data || [],
                        profilePicture: res.data?.profilePicture || undefined
                      }));
                      toast.success('Profile picture removed');
                    } catch (err) {
                      toast.error(err.response?.data?.message || 'Failed to remove profile picture');
                    }
                  }}
                  className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors"
                  title="Remove Profile Picture"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {!isLocked && (
                <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all ${
 application?.profilePicture
 ? 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
 : 'bg-violet-600 text-white hover:bg-violet-700'
 }`}>
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {application?.profilePicture ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      e.target.value = '';
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('File size must be less than 5MB');
                        return;
                      }
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('documentType', 'profilePicture');
                      try {
                        const res = await memberApplicationAPI.uploadPublicDocument(token, formData);
                        setApplication(prev => ({
                          ...prev,
                          documents: res.data?.data || [],
                          profilePicture: res.data?.profilePicture
                        }));
                        toast.success('Profile picture uploaded successfully');
                      } catch (err) {
                        toast.error(err.response?.data?.message || 'Upload failed');
                      }
                    }}
                  />
                </label>
              )}
            </div>
          </div>

          <RequirementOptionsPanel type={application} providedNames={(application?.documents || []).map(d => d.name)} className="mb-4" />
          {requiredDocs.length > 0 ? (
            <div className="space-y-4">
              {requiredDocs.map((docName) => {
                const uploaded = uploadedDocs.find(d => d.name === docName);
                const isUploading = !!uploadingSlots[docName];

                return (
                  <div
                    key={docName}
                    className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
 uploaded
 ? 'bg-emerald-50/50 border-emerald-100'
 : 'bg-slate-50/50 border-slate-200 border-dashed'
 }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {uploaded ? (
                        <div className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-slate-200 text-slate-400 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${uploaded ? 'text-emerald-800' : 'text-slate-700'}`}>
                          {docName}
                        </p>
                        {uploaded ? (
                          <p className="text-xs text-emerald-600 font-medium">Uploaded</p>
                        ) : (
                          <p className="text-xs text-slate-400 font-medium">Pending Upload</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      {uploaded && (
                        <button
                          type="button"
                          onClick={() => {
                            setViewerUrl(uploaded.url);
                            setViewerName(docName);
                            setViewerOpen(true);
                          }}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 transition-colors"
                          title="View document"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      )}
                      {uploaded && !isLocked && (
                        <button
                          type="button"
                          onClick={() => handleDelete(docName)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors"
                          title="Remove document"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                      {!isLocked && (
                        <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all ${
 isUploading
 ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
 : uploaded
 ? 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
 : 'bg-violet-600 text-white hover:bg-violet-700'
 }`}>
                          {isUploading ? (
                            <svg className="w-3 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          ) : (
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                          )}
                          {isUploading ? 'Uploading' : uploaded ? 'Replace' : 'Upload'}
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            disabled={isUploading}
                            onChange={e => handleFileUpload(docName, e)}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No required documents for this membership type.</p>
          )}

          {!isLocked ? (
            <div className="pt-4 border-t border-slate-100 flex justify-end">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full sm:w-auto px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-xl shadow-lg shadow-violet-250 transition-all flex items-center justify-center gap-2"
              >
                {isSubmitting && (
                  <svg className="w-4 h-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                Submit Documents for Approval
              </button>
            </div>
          ) : (
            <div className="bg-slate-50 rounded-xl p-4 text-center border border-slate-100">
              <p className="text-sm text-slate-500 font-medium">
                This application has been finalized or approved. Document modification is locked.
              </p>
            </div>
          )}
        </div>

        <DocumentViewerModal
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
          docUrl={viewerUrl}
          docName={viewerName}
        />
      </div>
    </div>
  );
};

export default PublicMembershipDocumentSubmit;
